import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Dropdown, FormRow, IconButton, Label, QueryDropdown, TextField } from 'basic-components';
import { Edit } from 'basic-components/icons';
import { DataLakeEnums, DataLakeReadableConsts } from 'dashboard-services/api/dataLake';
import { DropdownService, globalActions } from 'primary-components';

import { filesDatalakeActions } from 'actions/modules/datalake';
import getApiConfig from 'api/ApiConfig';

import { useQueryClient } from '@tanstack/react-query';
import { When } from 'react-if';

import classNames from 'classnames';
import './FileDetails.scss';

const EMPTY_ARRAY = [];
const EMPTY_OBJ = {};
const DEFAULT_NAME = "New Script"
const FileDetails = ({
  extension,
  errors = undefined,
  setFileInfo,
  newFile = false,
  fileInfo = EMPTY_OBJ,
  editable = false,
  areFileDetailsEditable,
  setAreFileDetailsEditable,
  groupNameLoc = undefined,
  ref
}) => {
  const dispatch = useDispatch(),
        deFaultGroup = useSelector(state => state.myAccountState.settingsState.defaultGroup),
        [fileName, setFileName] = useState(fileInfo?.fileName || `${DEFAULT_NAME}.${extension}`),
        [groupName, setGroupName] = useState(fileInfo.groupName || groupNameLoc || deFaultGroup),
        fileNameRef= useRef(),
        groupRef= useRef(),
        fileTypeRef= useRef(),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        queryClient = useQueryClient(),
        [groupQuery, setGroupQuery] = useState(),
        { fetchNextPage: fetchMoreGroups, isFetching: isFetchingGroups, isFetchingNextPage: isFetchingNextPageGroups, data: groups = EMPTY_ARRAY } = DropdownService.useQueryGroups({
          queryClient,
          input: groupQuery,
          getApiConfig: getApiConfigLocal,
          writeOnly: true
        }),
        localFileTypes = useMemo(() => dispatch(filesDatalakeActions.getFileTypes()), [dispatch]),
        defaultFiletype = useMemo(() => DataLakeReadableConsts.FILE_TYPES?.find(t => t?.value === fileInfo?.fileType) || fileInfo?.fileType, [fileInfo]),
        [fileType, setFileType] = useState(() => defaultFiletype?.value || DataLakeReadableConsts.FILE_TYPES.find(dl => dl.value === DataLakeEnums.FILE_TYPE.SOURCE)?.value),
        onChangeTypeObj = useCallback(value => setFileType(value), []),
        onFocus = useCallback(() => setFileName((value = "") => {
          if(value.endsWith("." + extension)) {
            const newValue = value
            return newValue.replace(/\.[^/.]+$/, "")
          } else {
            return value
          }
        }), [extension]),
        onBlur = useCallback(() => setFileName((value = "") => {
          const dotExtension = "." + extension
          if(value.endsWith(dotExtension) || value === "") {
            return value.trim()
          } else {
            return value.trim() + dotExtension
          }
        }), [extension]),
        onChangeGroup = useCallback((val, { data }) => {
          if(data?.isPublic) {
            dispatch(globalActions.getToastsStore()).warning("The selected group is public. Do you want to proceed?", "Selected group is public", {
              withCancel: false,
              confirmText: "Confirm",
              confirmAction: () => {
                setGroupName(val)
              },
              cancelAction: () => {
                groupRef.current.dropdown.current.setValue(groupName)
              }
            })
          } else {
            setGroupName(val)
          }
        }, [dispatch, groupName]),
        getGroupNameValidator = useMemo(() => [{
          message: errors?.find(e => e.field === `groupName`)?.message || "",
          validator: () => errors?.findIndex(e => e.field === `groupName`) === -1
        }], [errors]),
        onClickEdit = useCallback(() => setAreFileDetailsEditable(p => !p), [setAreFileDetailsEditable])

  useImperativeHandle(ref, () =>  ({
    validateForForm: () => fileNameRef.current?.validateForForm() && groupRef.current?.validateForForm() && fileTypeRef.current?.validateForForm(),
    isValid: () => groupRef.current?.isValid() && fileNameRef.current?.isValid() && fileTypeRef.current?.isValid()
  }))

  useEffect(() => {
    const fileNameRefStyle = fileNameRef.current.divWrapper.style
    if(!newFile && !areFileDetailsEditable) {
      fileNameRefStyle.width = "24px"
      fileNameRefStyle.minWidth = "24px"
      fileNameRefStyle.maxWidth = "696px"
      fileNameRefStyle.width = fileNameRef.current.divWrapper.children[0].scrollWidth + 4 + "px"
    } else if(!newFile && areFileDetailsEditable) {
      fileNameRefStyle.width = "256px"
      fileNameRefStyle.minWidth = "256px"
      fileNameRefStyle.maxWidth = "256px"
    }
  }, [areFileDetailsEditable, fileInfo, newFile, localFileTypes])

  useEffect(() => {
    if(areFileDetailsEditable) {
      setFileInfo(f => Object.assign({}, f, { fileName, fileType, groupName }))
    }
  }, [areFileDetailsEditable, fileName, fileType, groupName, setFileInfo])

  return (
    <div className={classNames("ng-file-details", { "ng-file-details--is-editable": areFileDetailsEditable && !newFile })}>
      <FormRow className="ng-file-details__top-row">
        <When condition={areFileDetailsEditable}>
          {() => (
            <Label 
                size="small"
            >
              File name:
            </Label>
          )}
        </When>
        <TextField
            defaultValue={fileName}
            disabled={!areFileDetailsEditable}
            name="fileName"
            onBlur={onBlur}
            onChange={setFileName}
            onFocus={onFocus}
            ref={fileNameRef}
            required
            value={fileName}
        />
        <When condition={!areFileDetailsEditable && editable}>
          {() => (
            <IconButton 
                className="ng-file-details__top-row__edit"
                onClick={onClickEdit}
            >
              <Edit
                  color="blue-bright"
                  height={16}
                  width={16}
              />
            </IconButton>
          )}
        </When>
      </FormRow>
      <FormRow className="ng-file-details__bottom-row">
        <Label
            className="ng-file-details__bottom-row__group-name-label"
            size="small"
        >
          Group name:
        </Label>
        <QueryDropdown
            defaultValue={groupName}
            disabled={!areFileDetailsEditable}
            fetchNextPage={fetchMoreGroups}
            isFetching={isFetchingGroups || isFetchingNextPageGroups}
            name="groupName"
            onChange={onChangeGroup}
            onInputChange={setGroupQuery}
            options={groups}
            placeholder="Select group"
            ref={groupRef}
            required
            validators={getGroupNameValidator}
            variant="line"
        />
        <Label
            className="ng-file-details__bottom-row__file-type-label"
            size="small"
        >
          File type:
        </Label>
        <Dropdown
            defaultValue={defaultFiletype}
            disabled={!areFileDetailsEditable}
            name="type"
            onChange={onChangeTypeObj}
            options={localFileTypes}
            placeholder="Select file type"
            ref={fileTypeRef}
            required
            variant="line"
        />
      </FormRow>
    </div>
  )
}

FileDetails.displayName="FileDetails"

FileDetails.propTypes = {
  areFileDetailsEditable: PropTypes.bool.isRequired,
  editable: PropTypes.bool,
  errors: PropTypes.array,
  extension: PropTypes.string.isRequired,
  fileInfo: PropTypes.object,
  groupNameLoc: PropTypes.string,
  newFile: PropTypes.bool,
  setAreFileDetailsEditable: PropTypes.func.isRequired,
  setFileInfo: PropTypes.func.isRequired
}

export default FileDetails
